import type { CSSProperties, ReactNode } from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

export function DiagnosticField({
  id,
  messages,
  indicatorLabel,
  children,
}: {
  id: string;
  messages: readonly string[];
  indicatorLabel: string;
  children: (invalid: boolean, describedBy?: string) => ReactNode;
}) {
  const uniqueMessages = [...new Set(messages)];
  const invalid = uniqueMessages.length > 0;
  const tooltipId = `${id}-diagnostic`;
  return (
    <span className="diagnostic-field">
      {children(invalid, invalid ? tooltipId : undefined)}
      {invalid && (
        <DiagnosticBadge
          id={id}
          messages={uniqueMessages}
          indicatorLabel={indicatorLabel}
        />
      )}
    </span>
  );
}

export function DiagnosticBadge({
  id,
  messages,
  indicatorLabel,
}: {
  id: string;
  messages: readonly string[];
  indicatorLabel: string;
}) {
  const tooltipId = `${id}-diagnostic`;
  const indicatorRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>();

  const updatePosition = useCallback(() => {
    const indicator = indicatorRef.current;
    const tooltip = tooltipRef.current;
    if (!indicator || !tooltip) return;
    const indicatorBounds = indicator.getBoundingClientRect();
    const tooltipBounds = tooltip.getBoundingClientRect();
    const gap = 8;
    const viewportPadding = 12;
    const spaceBelow = window.innerHeight - indicatorBounds.bottom;
    const spaceAbove = indicatorBounds.top;
    const preferredTop =
      spaceBelow >= tooltipBounds.height + gap + viewportPadding ||
      spaceBelow >= spaceAbove
        ? indicatorBounds.bottom + gap
        : indicatorBounds.top - tooltipBounds.height - gap;
    const top = Math.min(
      window.innerHeight - viewportPadding - tooltipBounds.height,
      Math.max(viewportPadding, preferredTop),
    );
    const centered = indicatorBounds.left + indicatorBounds.width / 2;
    const halfWidth = tooltipBounds.width / 2;
    const left = Math.min(
      window.innerWidth - viewportPadding - halfWidth,
      Math.max(viewportPadding + halfWidth, centered),
    );
    setPosition({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  const tooltip = (
    <div
      ref={tooltipRef}
      id={tooltipId}
      className="diagnostic-tooltip"
      role="tooltip"
      data-open={open && position !== undefined ? "true" : undefined}
      style={
        position
          ? ({
              "--diagnostic-tooltip-top": `${String(position.top)}px`,
              "--diagnostic-tooltip-left": `${String(position.left)}px`,
            } as CSSProperties)
          : undefined
      }
    >
      {messages.map((message) => (
        <div key={message}>{message}</div>
      ))}
    </div>
  );
  return (
    <span
      className="diagnostic-badge"
      onMouseEnter={() => {
        setOpen(true);
      }}
      onMouseLeave={() => {
        setOpen(false);
      }}
      onFocus={() => {
        setOpen(true);
      }}
      onBlur={() => {
        setOpen(false);
      }}
    >
      <span
        ref={indicatorRef}
        className="diagnostic-indicator"
        tabIndex={0}
        role="img"
        aria-label={indicatorLabel}
        aria-describedby={tooltipId}
      >
        !
      </span>
      {createPortal(tooltip, document.body)}
    </span>
  );
}
